<template>
  <header class="con-topbar">
    <div v-if="loading" class="con-topbar__status">Cargando...</div>

    <template v-else-if="park">
      <div class="con-topbar__left">
        <span class="con-topbar__section">Contratos</span>
        <span class="con-topbar__separator">·</span>
        <span class="con-topbar__park-name">{{ park.name }}</span>
        <span v-if="park.location" class="con-topbar__park-location">{{ park.location }}</span>
      </div>

      <div class="con-topbar__divider"></div>

      <div class="con-topbar__search-wrap">
        <div class="con-topbar__search" :class="{ 'con-topbar__search--active': searchActive }">
          <span class="mdi mdi-magnify con-topbar__search-icon"></span>
          <input
            ref="searchInputEl"
            :value="search"
            type="text"
            class="con-topbar__search-input"
            placeholder="Buscar inquilino, nave o contrato..."
            @focus="onSearchFocus"
            @blur="onSearchBlur"
            @input="onSearchInput"
          />
          <button v-if="search" class="con-topbar__search-clear" tabindex="-1" @mousedown.prevent="clearSearch">
            <span class="mdi mdi-close"></span>
          </button>
        </div>

        <div v-if="showDropdown" class="con-topbar__dropdown">
          <div v-if="searchLoading" class="con-topbar__dropdown-loading">Buscando...</div>
          <template v-else-if="hasResults">
            <div v-for="group in resultGroups" :key="group.key" class="con-topbar__dropdown-group">
              <div class="con-topbar__dropdown-label">{{ group.label }}</div>
              <button
                v-for="item in group.items"
                :key="item.id"
                class="con-topbar__dropdown-item"
                @mousedown.prevent="selectResult(group.key, item)"
              >
                <span class="con-topbar__dropdown-item-name">{{ item.name }}</span>
                <span class="con-topbar__dropdown-item-context">{{ item.context }}</span>
              </button>
            </div>
          </template>
          <div v-else class="con-topbar__dropdown-empty">Sin resultados</div>
        </div>
      </div>

      <div class="con-topbar__divider"></div>

      <div class="con-topbar__view-toggle">
        <button
          class="con-topbar__view-btn con-topbar__view-btn--left"
          :class="{ 'con-topbar__view-btn--active': view === 'list' }"
          title="Vista de lista"
          @click="$emit('update:view', 'list')"
        >
          <span class="mdi mdi-format-list-bulleted"></span>
        </button>
        <button
          class="con-topbar__view-btn con-topbar__view-btn--right"
          :class="{ 'con-topbar__view-btn--active': view === 'calendar' }"
          title="Vista de calendario"
          @click="$emit('update:view', 'calendar')"
        >
          <span class="mdi mdi-calendar-month-outline"></span>
        </button>
      </div>

      <div class="con-topbar__spacer"></div>

      <button class="con-topbar__import-btn" @click="$emit('import-contracts')">
        <span class="mdi mdi-file-upload-outline"></span>
        Importar
      </button>

      <button class="con-topbar__new-btn" @click="$emit('new-contract')">
        <span class="mdi mdi-plus"></span>
        Nuevo contrato
      </button>

      <div class="con-topbar__divider"></div>

      <div v-if="weather" class="con-topbar__weather">
        <span class="mdi con-topbar__weather-icon" :class="weather.icon"></span>
        <span class="con-topbar__weather-temp">{{ weather.temperature }}°C</span>
      </div>

      <GlobalAlertsChip :park-id="park?.id" />
      <NotificationBell :park-id="park?.id" />
    </template>
  </header>

  <!-- Barra de tabs — fuera del topbar navy, sobre el contenido -->
  <div v-if="park" class="con-tabsbar">
    <button
      v-for="tab in tabs"
      :key="tab.key"
      class="con-tabsbar__tab"
      :class="{ 'con-tabsbar__tab--active': activeTab === tab.key }"
      @click="$emit('update:activeTab', tab.key)"
    >
      <span v-if="tab.dot" class="con-tabsbar__tab-dot" :class="`con-tabsbar__tab-dot--${tab.dot}`"></span>
      <span class="con-tabsbar__tab-label">{{ tab.label }}</span>
      <span
        class="con-tabsbar__tab-count"
        :class="tab.semanticColor && counts[tab.key] > 0 ? `con-tabsbar__tab-count--${tab.semanticColor}` : ''"
      >({{ counts[tab.key] ?? 0 }})</span>
    </button>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import NotificationBell from '@/components/Global/NotificationBell.vue'
import GlobalAlertsChip from '@/components/Global/GlobalAlertsChip.vue'
import { getWeather } from '@/services/weather'

const props = defineProps({
  park: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  alerts: { type: Array, default: () => [] },
  parkId: { type: String, default: null },

  activeTab: { type: String, default: 'activos' },
  counts: {
    type: Object,
    default: () => ({
      todos: 0,
      activos: 0,
      porVencer: 0,
      negociacion: 0,
      vencidos: 0,
      historicos: 0,
    }),
  },

  view: { type: String, default: 'list' },

  search: { type: String, default: '' },

  searchResults: {
    type: Object,
    default: () => ({ inquilinos: [], naves: [], contratos: [] }),
  },
  searchLoading: { type: Boolean, default: false },
})

const emit = defineEmits([
  'update:activeTab',
  'update:view',
  'update:search',
  'search-select',
  'new-contract',
  'import-contracts',
  'open-notifications',
])

const tabs = [
  { key: 'activos', label: 'Activos' },
  { key: 'porVencer', label: 'Por vencer', dot: 'amber', semanticColor: 'amber' },
  { key: 'vencidos', label: 'Vencidos', dot: 'red', semanticColor: 'red' },
  { key: 'negociacion', label: 'En negociación' },
  { key: 'historicos', label: 'Históricos' },
  { key: 'todos', label: 'Todos' },
]

const searchInputEl = ref(null)
const searchFocused = ref(false)

const searchActive = computed(() => searchFocused.value || !!props.search)
const showDropdown = computed(() => searchFocused.value && props.search.length > 0)

const resultGroups = computed(() => {
  const groups = [
    { key: 'inquilinos', label: 'Inquilinos', items: props.searchResults.inquilinos || [] },
    { key: 'naves', label: 'Naves', items: props.searchResults.naves || [] },
    { key: 'contratos', label: 'Contratos', items: props.searchResults.contratos || [] },
  ]
  return groups.filter((g) => g.items.length > 0)
})

const hasResults = computed(() => resultGroups.value.length > 0)

function onSearchFocus() {
  searchFocused.value = true
}

function onSearchBlur() {
  setTimeout(() => {
    searchFocused.value = false
  }, 100)
}

function onSearchInput(event) {
  emit('update:search', event.target.value)
}

function clearSearch() {
  emit('update:search', '')
  searchInputEl.value?.focus()
}

function selectResult(groupKey, item) {
  emit('search-select', { type: groupKey, item })
}

const weather = ref(null)

watch(
  () => props.park,
  async (newPark) => {
    if (newPark?.latitude && newPark?.longitude) {
      weather.value = await getWeather(newPark.latitude, newPark.longitude)
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.con-topbar {
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  background: var(--side-panel);
  border-left: 1px solid var(--border-subtle);
  gap: 1rem;
  container-type: inline-size;
  container-name: con-topbar;
}

.con-topbar__status {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.con-topbar__divider {
  width: 1px;
  align-self: stretch;
  background: rgba(255, 255, 255, 0.15);
}

.con-topbar__spacer {
  flex: 1;
}

.con-topbar__left {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  flex-shrink: 1;
  min-width: 0;
  overflow: hidden;
}

.con-topbar__park-name {
  font-size: 0.9rem;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.con-topbar__section {
  font-family: var(--font-display);
  font-size: 1.1rem;
  font-weight: 500;
  color: var(--text-primary);
}

.con-topbar__separator {
  color: var(--text-muted);
  font-size: 0.9rem;
}

.con-topbar__park-location {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.5);
}

.con-topbar__search-wrap {
  position: relative;
  flex: 1 1 180px;
  min-width: 140px;
}

.con-topbar__search {
  display: flex;
  align-items: center;
  height: 32px;
  background: #24487a;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 0 0.6rem;
  gap: 0.4rem;
  transition: border-color 150ms ease, width 150ms ease;
}

.con-topbar__search--active {
  border-color: rgba(255, 255, 255, 0.6);
}

.con-topbar__search-icon {
  color: rgba(255, 255, 255, 0.5);
  font-size: 1rem;
  flex-shrink: 0;
}

.con-topbar__search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #ffffff;
  font-family: var(--font-body);
  font-size: 13px;
  min-width: 0;
}

.con-topbar__search-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.con-topbar__search-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 0;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.con-topbar__search-clear:hover {
  color: rgba(255, 255, 255, 0.8);
}

.con-topbar__dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  max-height: 360px;
  overflow-y: auto;
  z-index: 50;
}

.con-topbar__dropdown-loading,
.con-topbar__dropdown-empty {
  padding: 0.75rem;
  font-size: 0.8rem;
  color: var(--text-secondary, #64748b);
  text-align: center;
}

.con-topbar__dropdown-group + .con-topbar__dropdown-group {
  border-top: 1px solid #e2e8f0;
}

.con-topbar__dropdown-label {
  padding: 0.5rem 0.75rem 0.25rem;
  font-family: var(--font-body);
  font-size: 0.68rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
}

.con-topbar__dropdown-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 0.4rem 0.75rem;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
}

.con-topbar__dropdown-item:hover {
  background: #f8fafc;
}

.con-topbar__dropdown-item-name {
  font-family: var(--font-body);
  font-size: 0.83rem;
  font-weight: 500;
  color: #1e3a5f;
}

.con-topbar__dropdown-item-context {
  font-family: var(--font-body);
  font-size: 0.74rem;
  color: #64748b;
}

.con-topbar__view-toggle {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.con-topbar__view-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  font-size: 16px;
  transition: background 200ms ease, color 200ms ease;
}

.con-topbar__view-btn:hover {
  color: rgba(255, 255, 255, 0.6);
}

.con-topbar__view-btn--left {
  border-radius: 6px 0 0 6px;
}

.con-topbar__view-btn--right {
  border-radius: 0 6px 6px 0;
}

.con-topbar__view-btn--active {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

.con-topbar__view-btn--active:hover {
  color: #ffffff;
}

.con-topbar__new-btn {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  height: 32px;
  background: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 0 0.75rem;
  color: #1e3a5f;
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 100ms ease;
  flex-shrink: 0;
}

.con-topbar__new-btn:hover {
  background: #f1f5f9;
}

.con-topbar__import-btn {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  height: 32px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 6px;
  padding: 0 0.75rem;
  margin-right: 0.5rem;
  color: #ffffff;
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;
}

.con-topbar__import-btn:hover {
  background: rgba(255, 255, 255, 0.22);
}

.con-topbar__weather {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--text-secondary);
  flex-shrink: 0;
}

.con-topbar__weather-icon {
  font-size: 1.2rem;
}

.con-topbar__weather-temp {
  font-family: var(--font-mono);
  font-size: 0.9rem;
}

.con-topbar__notif {
  position: relative;
  display: flex;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.1rem;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
}

.con-topbar__notif-badge {
  position: absolute;
  top: -6px;
  right: -8px;
  background: var(--accent-blue);
  color: white;
  font-size: 0.6rem;
  font-family: var(--font-mono);
  padding: 0 4px;
  border-radius: 8px;
  min-width: 14px;
  text-align: center;
}

.con-topbar__notif-badge--critical {
  background: var(--accent-red);
}

/* --- Barra de tabs, debajo del topbar --- */
.con-tabsbar {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.5rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.con-tabsbar__tab {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  background: transparent;
  border: none;
  border-radius: 6px;
  padding: 0.4rem 0.65rem;
  cursor: pointer;
  position: relative;
  transition: background 100ms ease;
}

.con-tabsbar__tab:hover {
  background: #f1f5f9;
}

.con-tabsbar__tab-label {
  font-family: var(--font-body);
  font-size: 0.8rem;
  color: #475569;
}

.con-tabsbar__tab-count {
  font-family: var(--font-mono);
  font-size: 0.75rem;
  color: #94a3b8;
}

.con-tabsbar__tab--active {
  background: #eff6ff;
}

.con-tabsbar__tab--active::after {
  content: '';
  position: absolute;
  left: 0.5rem;
  right: 0.5rem;
  bottom: -1px;
  height: 2px;
  background: #1e3a5f;
  border-radius: 2px;
}

.con-tabsbar__tab--active .con-tabsbar__tab-label {
  color: #1e3a5f;
  font-weight: 500;
}

.con-tabsbar__tab--active .con-tabsbar__tab-count {
  color: #1e3a5f;
}

.con-tabsbar__tab-count--amber {
  color: #d97706 !important;
}

.con-tabsbar__tab-count--red {
  color: #dc2626 !important;
}

.con-tabsbar__tab-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.con-tabsbar__tab-dot--amber {
  background: #f59e0b;
}

.con-tabsbar__tab-dot--red {
  background: #ef4444;
}

/* Responsive al ancho real disponible, sin importar la causa (sidebar, ventana, etc.) */
@container con-topbar (max-width: 820px) {
  .con-topbar__park-location {
    display: none;
  }
}

@container con-topbar (max-width: 680px) {
  .con-topbar__weather {
    display: none;
  }
}

@container con-topbar (max-width: 560px) {
  .con-topbar__new-btn span:not(.mdi) {
    display: none; /* deja solo el ícono +, sin el texto "Nuevo contrato" */
  }
  .con-topbar__new-btn {
    padding: 0 0.5rem;
  }
}
</style>